/**
 * Verbes métier du domaine facturation.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import { computeReceivables } from "./types.js";
import type {
  InvoiceDetail,
  InvoiceStatus,
  InvoiceSummary,
  Payment,
} from "./types.js";

export const listInvoicesInput = z.object({
  confirmationId: z.string().optional(),
  status: z
    .array(
      z.enum([
        "draft",
        "sent",
        "partially_paid",
        "paid",
        "overdue",
        "disputed",
        "cancelled",
        "credit_note_issued",
      ]),
    )
    .optional(),
  clientId: z.string().uuid().optional(),
});

export type ListInvoicesInput = z.infer<typeof listInvoicesInput>;

export const readInvoiceInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid(),
});

export type ReadInvoiceInput = z.infer<typeof readInvoiceInput>;

export const recordPaymentInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid(),
  amount: z.coerce.number().positive("Le montant doit être supérieur à zéro"),
  paymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ"),
  // `delegation` : la part que le client a payée directement au
  // sous-traitant (loi de 1975, délégation ou paiement direct). Elle solde la
  // facture sans entrer dans la trésorerie.
  paymentMethod: z
    .enum(["virement", "cheque", "espece", "carte", "prelevement", "delegation", "autre"])
    .default("virement"),
  reference: z.string().nullish(),
  notes: z.string().nullish(),
  /** Autorise explicitement un encaissement supérieur au solde. */
  allowOverpayment: z.boolean().default(false),
});

export type RecordPaymentInput = z.infer<typeof recordPaymentInput>;

export interface RecordPaymentCommand {
  invoiceId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference: string | null;
  notes: string | null;
}

export interface InvoiceFilter {
  status?: InvoiceStatus[];
  clientId?: string;
}

export interface InvoiceRepository {
  list(ctx: ExecutionContext, filter?: InvoiceFilter): Promise<InvoiceSummary[]>;
  findById(ctx: ExecutionContext, invoiceId: string): Promise<InvoiceDetail | null>;
  recordPayment(ctx: ExecutionContext, command: RecordPaymentCommand): Promise<Payment>;
}

export function createInvoiceServices(repo: InvoiceRepository) {
  const listerFactures = defineService<ListInvoicesInput, InvoiceSummary[]>({
    name: "lister_factures",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, { status: input.status, clientId: input.clientId }),
  });

  const lireFacture = defineService<ReadInvoiceInput, InvoiceDetail>({
    name: "lire_facture",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const invoice = await repo.findById(ctx, input.invoiceId);
      if (!invoice) {
        throw new DomainError("not_found", "Cette facture n'existe pas.", {
          invoiceId: input.invoiceId,
        });
      }
      return invoice;
    },
  });

  /**
   * Répond à « qui doit me payer ? ».
   *
   * Trié par ancienneté de retard : une facture en souffrance depuis trois
   * mois se recouvre moins bien qu'une échue d'hier, elle passe donc devant.
   */
  const argentQuiDort = defineService<
    { confirmationId?: string },
    { invoices: InvoiceSummary[]; totalDue: number; overdueDue: number; overdueCount: number }
  >({
    name: "lister_impayes",
    level: ActionLevel.Read,
    handler: async (ctx) => {
      const all = await repo.list(ctx);
      const unpaid = all
        .filter((i) => !i.balance.isSettled)
        .sort((a, b) => b.balance.daysLate - a.balance.daysLate);

      return { invoices: unpaid, ...computeReceivables(all) };
    },
  });

  const enregistrerPaiement = defineService<RecordPaymentInput, Payment>({
    name: "enregistrer_paiement",
    // Réversible : un encaissement mal saisi s'annule en supprimant la ligne.
    // C'est l'envoi de la facture au client, lui, qui serait N3.
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      const invoice = await repo.findById(ctx, input.invoiceId);
      if (!invoice) {
        throw new DomainError("not_found", "Cette facture n'existe pas.", {
          invoiceId: input.invoiceId,
        });
      }

      if (invoice.status === "cancelled") {
        throw new DomainError(
          "conflict",
          "Cette facture est annulée : elle ne peut pas recevoir de paiement.",
          { invoiceId: input.invoiceId },
        );
      }

      // Un encaissement supérieur au solde est presque toujours une faute de
      // frappe. On refuse par défaut plutôt que de créer un trop-perçu qu'il
      // faudra retrouver à la clôture — mais on laisse le passage forcé, parce
      // qu'un client qui paie deux factures en un virement, ça existe.
      const excess = input.amount - invoice.balance.remaining;
      if (excess > 0.009 && !input.allowOverpayment) {
        throw new DomainError(
          "invalid_input",
          `Ce paiement dépasse le solde de ${excess.toFixed(2)} €. ` +
            `Le reste dû est de ${invoice.balance.remaining.toFixed(2)} €. ` +
            `Confirmez si le trop-perçu est voulu.`,
          {
            invoiceId: input.invoiceId,
            remaining: invoice.balance.remaining,
            excess: Math.round(excess * 100) / 100,
          },
        );
      }

      return repo.recordPayment(ctx, {
        invoiceId: input.invoiceId,
        amount: input.amount,
        paymentDate: input.paymentDate,
        paymentMethod: input.paymentMethod,
        reference: input.reference ?? null,
        notes: input.notes ?? null,
      });
    },
  });

  return { listerFactures, lireFacture, argentQuiDort, enregistrerPaiement };
}

export type InvoiceServices = ReturnType<typeof createInvoiceServices>;
